import { setHoneycombTracer } from 'app/libs/honeycomb'
import { HttpMethod, request } from 'app/networking/fetch'
import configuration from 'config/configuration'

const baseUrl = configuration.apiGatewayUrl

export type CommunicationPreferencesDTO = {
  id: string
  email: string
  userServiceId: string
  trainerServiceId?: string | null
  smsEnabled?: boolean
  systemSmsEnabled?: boolean
  trainerSmsEnabled?: boolean
}

export type CommunicationPreferencesPayload = {
  marketingSmsOptIn: boolean
  paymentNotificationsOptIn: boolean
  transactionalSmsOptIn: boolean
  yourTrainerMessagesOptIn: boolean
  allTrainersMessagesOptIn: boolean
}

export type ContactSmsSettings = {
  smsEnabled: boolean
  systemSmsEnabled: boolean
  trainerSmsEnabled: boolean
}

const endpointForUser = (userId: string) =>
  `/communication/v1/contact/users/${userId}/contact-preferences`

const contactBasePath = (contactId: string) =>
  `/communication/v1/contact/${encodeURIComponent(contactId)}`

export const defaultCommunicationPreferences =
  (): CommunicationPreferencesPayload => ({
    marketingSmsOptIn: true,
    paymentNotificationsOptIn: true,
    transactionalSmsOptIn: true,
    yourTrainerMessagesOptIn: true,
    allTrainersMessagesOptIn: true,
  })

export const contactPreferencesToPayload = (
  api: CommunicationPreferencesDTO | null | undefined,
): CommunicationPreferencesPayload => ({
  ...defaultCommunicationPreferences(),

  marketingSmsOptIn: api?.smsEnabled ?? true,

  transactionalSmsOptIn: api?.systemSmsEnabled ?? true,

  allTrainersMessagesOptIn: api?.trainerSmsEnabled ?? true,

  yourTrainerMessagesOptIn: api?.trainerSmsEnabled ?? true,

  paymentNotificationsOptIn: true,
})

export const payloadToContactSmsSettings = (
  payload: CommunicationPreferencesPayload,
): ContactSmsSettings => ({
  smsEnabled: payload.marketingSmsOptIn,
  systemSmsEnabled: payload.transactionalSmsOptIn,
  trainerSmsEnabled: payload.allTrainersMessagesOptIn,
})

export const getCommunicationPreferences = async (
  userId: string,
): Promise<CommunicationPreferencesDTO | null> => {
  try {
    const response = await request({
      baseUrl,
      endpoint: endpointForUser(userId),
      method: 'GET' as HttpMethod,
    })

    if (response.status === 404) {
      return null
    }

    const result = await response.json()

    return response.ok
      ? (result as CommunicationPreferencesDTO)
      : Promise.reject(result.message)
  } catch (error: any) {
    const errorMessage =
      error instanceof Error
        ? error.message
        : 'Failed to load communication preferences'

    setHoneycombTracer({
      tracerName: 'default',
      spanName: 'communication-preferences',
      attributeName: 'get-communication-preferences',
      value: new Error(errorMessage),
    })

    throw new Error(errorMessage)
  }
}

const putContactSmsFlag = async (
  contactId: string,
  path: string,
  queryKey: string,
  value: boolean,
): Promise<void> => {
  const response = await request({
    baseUrl,
    endpoint: `${contactBasePath(contactId)}${path}?${queryKey}=${String(
      value,
    )}`,
    method: 'PUT' as HttpMethod,
  })

  if (!response.ok) {
    const result = await response.json()
    return Promise.reject(result.message)
  }
}

export const removeTrainerAuthToSend = async (
  trainerServiceId: string,
  contactIdToRemove: string,
): Promise<void> => {
  const response = await request({
    baseUrl,
    endpoint: `/communication/v1/contact/trainer/${encodeURIComponent(
      trainerServiceId,
    )}/auth-to-send/${encodeURIComponent(contactIdToRemove)}`,
    method: 'PUT' as HttpMethod,
  })

  if (!response.ok) {
    const result = await response.json()
    return Promise.reject(result.message)
  }
}

export const updateCommunicationPreferences = async ({
  contactId,
  trainerServiceId,
  currentPreferences,
  savedPreferences,
}: {
  contactId: string
  trainerServiceId?: string | null
  currentPreferences: CommunicationPreferencesPayload
  savedPreferences: CommunicationPreferencesPayload
}): Promise<void> => {
  const settings = payloadToContactSmsSettings(currentPreferences)

  const requests: Promise<void>[] = [
    putContactSmsFlag(
      contactId,
      '/sms-enabled',
      'smsEnabled',
      settings.smsEnabled,
    ),

    putContactSmsFlag(
      contactId,
      '/system-sms-enabled',
      'systemSmsEnabled',
      settings.systemSmsEnabled,
    ),

    putContactSmsFlag(
      contactId,
      '/trainer-sms-enabled',
      'trainerSmsEnabled',
      settings.trainerSmsEnabled,
    ),
  ]

  const removedYourTrainerMessages =
    savedPreferences.yourTrainerMessagesOptIn &&
    !currentPreferences.yourTrainerMessagesOptIn

  if (removedYourTrainerMessages && trainerServiceId) {
    requests.push(removeTrainerAuthToSend(trainerServiceId, contactId))
  }

  await Promise.all(requests)
}
